import { useTranslation } from "react-i18next";
import { Select } from "../../../components/ui/Select";
import { useActiveLang } from "../../../domains/app/hooks/useActiveLang";
import { getCountriesListSortedByPopularity } from "../../../domains/app/utils/countries";

/**
 * The country question as a labelled dropdown, for the desktop screen that asks
 * it alongside the name (Figma 7092-5913).
 *
 * The mobile slide is a full-screen list with its search in the footer band,
 * because it is the only thing on that screen. Here it shares the screen with
 * two name fields, so it collapses into the DS `Select` — same option set, same
 * popularity ordering, and the search moves inside the panel where the
 * component already puts it (Figma "D7.identite.pays-ouvert").
 */
export function OnboardingCountryField({
  country,
  onCountryChange,
}: {
  /** ISO code, or the empty string before anything is chosen. */
  country: string;
  onCountryChange: (country: string) => void;
}): React.JSX.Element {
  const { t } = useTranslation();
  const { activeLang } = useActiveLang();

  // The flag rides in the label, so the closed trigger reads exactly as the
  // design draws it with no custom trigger. Two spaces after it, as drawn: one
  // is swallowed by the emoji's own advance, and the name touched the flag.
  const options = getCountriesListSortedByPopularity(activeLang).map(
    (option) => ({ ...option, label: `${option.flag}  ${option.name}` })
  );

  return (
    <div className="flex w-full flex-col gap-1">
      <label className="font-dm-sans text-ui-note text-ds-black">
        {t("Country")}
      </label>
      <Select
        appearance="field"
        footerAction={null}
        searchOnlyOptions={null}
        value={country}
        onChange={(option) => {
          onCountryChange(option.value);
        }}
        options={options}
        placeholder={t("Select your country")}
        searchPlaceholder={t("Search countries")}
        searchable
        variant="ghost"
        className="rounded-xl border-ds-slate-200 bg-white"
      />
    </div>
  );
}
